'use client';

import type { ResponsiveImageData } from '@/components/atoms/ResponsiveImage';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel';
import NextImage from 'next/image';

interface ProductImageCarouselProps {
    images: ResponsiveImageData[];
    title: string;
}

export default function ProductImageCarousel({ images, title }: ProductImageCarouselProps) {
    if (images.length === 0) {
        return null;
    }

    const isSingleImage = images.length === 1;

    return (
        <div className="w-full">
            {isSingleImage ? (
                // Single image layout - no carousel needed
                <div className="aspect-square w-full overflow-hidden bg-haug-ivory-100">
                    <NextImage
                        src={images[0].urlMedium || ''}
                        alt={images[0].alt || title}
                        width={images[0].widthMedium || 600}
                        height={images[0].heightMedium || 600}
                        className="h-full w-full object-cover"
                        priority
                    />
                </div>
            ) : (
                // Multiple images layout - carousel
                <Carousel
                    opts={{
                        align: 'start',
                        loop: false,
                        dragFree: true,
                        containScroll: 'trimSnaps',
                    }}
                    className="w-full"
                >
                    <CarouselContent className="-ml-2 md:-ml-4">
                        {images.map((image, index) => (
                            <CarouselItem
                                key={image.id || index}
                                className="basis-[90%] pl-2 sm:basis-[85%] md:basis-[100%] md:pl-4"
                            >
                                <div className="aspect-square w-full overflow-hidden bg-haug-ivory-100">
                                    <NextImage
                                        src={image.urlMedium || ''}
                                        alt={image.alt || `${title} - Image ${index + 1}`}
                                        width={image.widthMedium || 600}
                                        height={image.heightMedium || 600}
                                        className="h-full w-full object-cover"
                                        priority={index === 0}
                                    />
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <CarouselPrevious
                        size="lg"
                        className="-left-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                    />
                    <CarouselNext
                        size="lg"
                        className="-right-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                    />
                </Carousel>
            )}
        </div>
    );
}
